import { useMemo } from "react";
import { ArrowDown, ArrowUp, Plus, Sparkles, Trash2 } from "lucide-react";
import type { SectionContentBrief } from "./simplified-editor-guidance";

interface QuickField {
  key: string;
  label: string;
  value: string;
}

interface MobileEditorSheetProps {
  sectionLabel: string;
  canMove: boolean;
  moveDirection: "up" | "down";
  quickFields: QuickField[];
  sectionBrief: SectionContentBrief | null;
  onFieldChange: (key: string, value: string) => void;
  onMove: () => void;
  onDelete: () => void;
  onAddSection: () => void;
  onOpenFullControls: () => void;
}

export function MobileEditorSheet(props: MobileEditorSheetProps) {
  const helperText = useMemo(() => {
    if (props.quickFields.length > 0) {
      return "Update the main copy here, then switch to full controls for deeper design settings.";
    }

    return "This section does not expose quick text fields yet. Use full controls for advanced editing.";
  }, [props.quickFields.length]);

  return (
    <div className="fixed inset-x-0 bottom-0 z-30 rounded-t-[28px] border border-border bg-background/95 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 shadow-2xl backdrop-blur">
      <div className="mx-auto mb-3 h-1.5 w-14 rounded-full bg-muted" />
      <div className="space-y-4">
        <div className="space-y-1">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">Mobile editor</p>
          <h3 className="text-base font-semibold text-foreground">{props.sectionLabel}</h3>
          <p className="text-sm leading-6 text-muted-foreground">{helperText}</p>
        </div>

        {props.quickFields.length > 0 ? (
          <div className="space-y-3">
            {props.quickFields.map((field) => (
              <label key={field.key} className="block space-y-1.5">
                <span className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">{field.label}</span>
                <textarea
                  value={field.value}
                  rows={field.value.length > 120 ? 4 : 2}
                  onChange={(event) => props.onFieldChange(field.key, event.target.value)}
                  className="min-h-12 w-full rounded-2xl border border-input bg-background px-3 py-2.5 text-sm text-foreground outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/15"
                />
              </label>
            ))}
          </div>
        ) : null}

        {props.sectionBrief ? (
          <div className="rounded-2xl border border-[var(--color-ai)]/20 bg-[var(--color-ai)]/5 px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--color-ai)]">
              {props.sectionBrief.title}
            </p>
            <p className="mt-2 text-sm leading-6 text-foreground">{props.sectionBrief.instruction}</p>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">{props.sectionBrief.example}</p>
          </div>
        ) : null}

        <div className="flex justify-end">
          <button
            type="button"
            onClick={props.onAddSection}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl border border-border bg-card px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-accent"
          >
            <Plus className="h-4 w-4" />
            Add section
          </button>
        </div>

        <div className="grid grid-cols-3 gap-2" data-simplified-section-actions="3">
          <button
            type="button"
            onClick={props.onMove}
            disabled={!props.canMove}
            className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-border bg-card px-3 py-3 text-sm font-medium text-foreground transition hover:bg-accent disabled:opacity-50"
          >
            {props.moveDirection === "up" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
            {props.moveDirection === "up" ? "Move up" : "Move down"}
          </button>
          <button
            type="button"
            onClick={props.onDelete}
            className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-destructive/25 bg-destructive/5 px-3 py-3 text-sm font-medium text-destructive transition hover:bg-destructive/10"
          >
            <Trash2 className="h-4 w-4" />
            Remove
          </button>
          <button
            type="button"
            onClick={props.onOpenFullControls}
            className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-primary px-3 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
          >
            <Sparkles className="h-4 w-4" />
            Full controls
          </button>
        </div>
      </div>
    </div>
  );
}
